import './styles.css';
import type { ContextBlock, ConversationTurn, DraftRequest, ProjectFileRef } from './pipeline/types';
import { PIPELINE_STAGES } from './pipeline/registry';
import { runPipeline, type PipelineRunReport } from './pipeline/runner';
import { effectiveEnabledMap, isStageEffectivelyEnabled } from './pipeline/settings';
import {
  buildRequestBody,
  buildSystemBlocks,
  sendMessageOnce,
  streamMessage,
  type AnthropicContentBlock,
  type AnthropicMessage,
  type AnthropicRequestBody,
  type UsageInfo,
} from './api/anthropic';
import { AGENT_TOOLS, executeTool, type ToolExecutionContext } from './agent/tools';
import { AGENT_PERSONAS, getPersona, type AgentPersona } from './agent/personas';
import {
  authorReconciliationWithLLM,
  buildHeuristicReconciliation,
  ensurePersonaHarness,
  planAgentDispatch,
  type DispatchPlanEntry,
} from './agent/contexttrees';
import { equipAgent, type AgentDescriptor, type Director, type MicroSession } from 'contexttrees';
import {
  createContextTreesProject,
  getActiveContextTreesProjectId,
  listContextTreesProjects,
  loadContextTreesDirector,
  saveContextTreesDirector,
  setActiveContextTreesProjectId,
} from './db/contexttrees-repo';
import { reviewSessionOutcome } from './ui/contexttrees-review';
import { costForUsage, SUMMARIZATION_MODEL } from './api/pricing';
import { estimateTokens } from './pipeline/token-estimate';
import { draftText } from './pipeline/draft-text';
import { truncateContextToTokenBudget } from './pipeline/naive-truncate';
import {
  getBenchmarkRuns,
  resolveContextBlock,
  saveBenchmarkRun,
  type BenchmarkRunRecord,
  type BenchmarkVariantRecord,
} from './db/benchmark-runs-repo';
import {
  getApiKey,
  getContextTreesSettings,
  getOptimizationSettings,
  getSelectedModel,
  saveApiKey,
  saveSelectedModel,
} from './db/settings-repo';
import { getFile, saveFile } from './db/files-repo';
import { getGitHubToken, saveGitHubToken } from './db/github-repo';
import {
  newConversation,
  saveConversation,
  getConversation,
  getAllConversations,
  type ConversationRecord,
} from './db/conversations-repo';
import { FileExplorer } from './ui/file-explorer';
import { CodeEditor } from './ui/editor';
import { ChatPane } from './ui/chat-pane';
import { OptimizationPanel, type SessionStats } from './ui/optimization-panel';
import { AccountModal } from './ui/account-modal';
import { AccountPanel, type AccountPanelCallbacks } from './ui/account-panel';
import { BenchmarksPanel } from './ui/benchmarks-panel';
import { NavRail } from './ui/nav-rail';
import { MobileTabBar } from './ui/mobile-tabbar';
import { initTheme } from './ui/theme';
import { setView } from './ui/view';
import { openModal } from './ui/modal';
import { pullAll, pushBenchmarks, pushConversation, pushSettings } from './account/sync';
import { getStoredSession } from './account/session';
import { getCumulativeBenchmarkStats, recordBenchmarkDelta } from './db/benchmarks-repo';
import { computeLanguageMix, pushTelemetry } from './telemetry/client';
import { updateSymbolIndexForFile, clearSymbolIndex } from './parsing/symbol-index';
import { updateChunkIndexForFile, clearChunkIndex } from './nlp/chunk-index';
import { warmEmbedder } from './nlp/embeddings';
import { warmCompressor } from './nlp/compressor';
import { looksLikeUnifiedDiff, tryApplyUnifiedDiff } from './parsing/apply-diff';

function debounce<Args extends unknown[]>(fn: (...args: Args) => void, ms: number): (...args: Args) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

const SYSTEM_PROMPT =
  'You are a pair-programming assistant embedded in a lightweight IDE. ' +
  'The context you receive has been pre-processed to minimize tokens — it may contain only relevant ' +
  'extracted code, dependency signatures, or diffs rather than full files. Work with what you are given.';

const MAX_OUTPUT_TOKENS = 4096;

async function main() {
  initTheme();
  const app = document.getElementById('app')!;

  const fileExplorerEl = document.getElementById('file-explorer')!;
  const editorHostEl = document.getElementById('editor-host')!;
  const chatPaneEl = document.getElementById('chat-pane')!;
  const optimizationPaneEl = document.getElementById('optimization-panel')!;

  let apiKey = await getApiKey();
  let settings = await getOptimizationSettings();
  let selectedModel = await getSelectedModel();
  let conversation: ConversationRecord = newConversation();

  const openFiles = new Map<string, ProjectFileRef>();
  // Tracks when each open file was last opened/edited — used by "Trim context" to close the
  // least-recently-touched files first rather than an arbitrary or insertion order.
  const fileTouchedAt = new Map<string, number>();
  const sessionStats: SessionStats = {
    requests: 0,
    tokensSentEstimate: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    estimatedCostUsd: 0,
    baselineTokensEstimate: 0,
  };

  // ---------- API key bar ----------
  const apiKeyBar = document.createElement('div');
  apiKeyBar.id = 'api-key-bar';
  const apiKeyInput = document.createElement('input');
  apiKeyInput.type = 'password';
  apiKeyInput.placeholder = 'Anthropic API key (sk-ant-…) — stored only in this browser';
  apiKeyInput.value = apiKey;
  apiKeyInput.addEventListener('change', () => {
    apiKey = apiKeyInput.value.trim();
    void saveApiKey(apiKey);
    void navRail.refresh();
  });
  apiKeyBar.appendChild(apiKeyInput);
  optimizationPaneEl.appendChild(apiKeyBar);

  // ---------- Shell (left nav rail + mobile tab bar) ----------
  const navRail = new NavRail(document.getElementById('rail-nav')!, document.getElementById('rail-footer')!, {
    onOpenAccount: () => accountModal.open(),
  });
  new MobileTabBar(app);
  setView('view-chat');

  // ---------- Account (sync) ----------
  const accountCallbacks: AccountPanelCallbacks = {
    getCurrentApiKey: () => apiKey,
    onApiKeyReceived: (key) => {
      apiKey = key;
      apiKeyInput.value = key;
      void saveApiKey(key);
      void navRail.refresh();
    },
    onLoggedIn: () => {
      // Settings/conversations land in IndexedDB; reloading is the simplest way to get every
      // already-constructed UI component (panel, chat pane, conversation list) to pick them up.
      void pullAll().then(() => location.reload());
    },
    getCurrentGithubToken: () => getGitHubToken(),
    onGithubTokenReceived: (token) => {
      void saveGitHubToken(token);
    },
  };

  const accountTriggerEl = document.createElement('div');
  accountTriggerEl.id = 'account-trigger';
  optimizationPaneEl.appendChild(accountTriggerEl);
  const accountModal = new AccountModal(accountTriggerEl, accountCallbacks);

  // The mobile shell has a dedicated Account tab rather than a modal — same panel, mounted inline.
  new AccountPanel(document.getElementById('account-view-body')!, {
    ...accountCallbacks,
    onStateChanged: () => void navRail.refresh(),
  });

  // ---------- Benchmarks (this device + across your devices) ----------
  const benchmarksPanelEl = document.createElement('div');
  benchmarksPanelEl.id = 'benchmarks-panel';
  optimizationPaneEl.appendChild(benchmarksPanelEl);
  const benchmarksPanel = new BenchmarksPanel(benchmarksPanelEl, await getCumulativeBenchmarkStats());

  const optimizationInner = document.createElement('div');
  optimizationPaneEl.appendChild(optimizationInner);

  // ---------- Editor ----------
  const a3Stage = PIPELINE_STAGES.find((s) => s.id === 'A3')!;
  let embedderWarmupScheduled = false;
  function warmEmbedderIfEnabled() {
    if (embedderWarmupScheduled || !isStageEffectivelyEnabled(settings, a3Stage)) return;
    embedderWarmupScheduled = true;
    // Deferred so the ~23MB embedding runtime download never competes with
    // initial page load. Re-checks the toggle right before actually firing —
    // scheduling alone must not commit to the download if the user disables
    // A3 within this window; most users who leave it on will still have it
    // warm well before their first send, since typing a message and opening
    // a file both take longer than 2s.
    setTimeout(() => {
      if (isStageEffectivelyEnabled(settings, a3Stage)) warmEmbedder();
      else embedderWarmupScheduled = false; // allow warmEmbedderIfEnabled() to reschedule if re-enabled later
    }, 2000);
  }
  warmEmbedderIfEnabled();

  const b4Stage = PIPELINE_STAGES.find((s) => s.id === 'B4')!;
  let compressorWarmupScheduled = false;
  function warmCompressorIfEnabled() {
    if (compressorWarmupScheduled || !isStageEffectivelyEnabled(settings, b4Stage)) return;
    compressorWarmupScheduled = true;
    // Same deferral rationale as the embedder: spinning up the compression
    // worker (and, once one exists, fetching its model) must not compete with
    // initial page load, and the toggle is re-checked right before firing.
    setTimeout(() => {
      if (isStageEffectivelyEnabled(settings, b4Stage)) warmCompressor();
      else compressorWarmupScheduled = false; // allow rescheduling if B4 is re-enabled later
    }, 2000);
  }
  warmCompressorIfEnabled();

  const reindexDebounced = debounce(
    (path: string, content: string, languageId: ProjectFileRef['languageId']) => {
      void updateSymbolIndexForFile(path, content, languageId);
      void updateChunkIndexForFile(path, content, languageId);
    },
    500,
  );

  const editor = new CodeEditor(editorHostEl, {
    onChange: (path, content) => {
      const existing = openFiles.get(path);
      if (existing) openFiles.set(path, { ...existing, content });
      fileTouchedAt.set(path, Date.now());
      // Preserves fields the in-memory ProjectFileRef doesn't carry (local FS handle, GitHub blob
      // sha/synced-hash) — saveFile() replaces the whole stored record, so skipping this would
      // silently break "commit to GitHub" (loses the sha) on every keystroke.
      void (async () => {
        const stored = await getFile(path);
        await saveFile({ ...stored, path, content, languageId: existing?.languageId ?? stored?.languageId, updatedAt: Date.now() });
      })();
      reindexDebounced(path, content, existing?.languageId);
    },
    onCursorMove: () => {
      /* cursor is read on-demand at send time via editor.getCursor() */
    },
  });

  // ---------- File explorer ----------
  new FileExplorer(fileExplorerEl, {
    onOpenFile: (path) => {
      void (async () => {
        const record = await getFile(path);
        if (!record) return;
        editor.openFile(path, record.content, record.languageId);
        openFiles.set(path, { path, content: record.content, languageId: record.languageId });
        fileTouchedAt.set(path, Date.now());
      })();
    },
    onFilesLoaded: (files, isNewProject) => {
      if (isNewProject) {
        // A different folder just replaced the workspace — without this, files opened from the
        // previous project would stay in the AI's context (and the symbol/chunk indexes) forever,
        // silently mixed in with the new project's files on every future request.
        openFiles.clear();
        fileTouchedAt.clear();
        editor.reset();
        clearSymbolIndex();
        clearChunkIndex();
        // C1 diffs by relative path — if the new project happens to share a path with the old one
        // (e.g. both have "src/index.js"), a stale sentFiles entry would make C1 diff against
        // completely unrelated content from the previous project.
        conversation.sentFiles = {};
      }
      // Bulk-(re)build the project-wide symbol index (A2) and chunk/embedding
      // index (A3) whenever the loaded file set changes — initial load,
      // folder open, or drop. Both run fully in the background.
      for (const f of files) {
        void updateSymbolIndexForFile(f.path, f.content, f.languageId);
        void updateChunkIndexForFile(f.path, f.content, f.languageId);
      }
      void navRail.refresh(); // file count / workspace card
    },
  });

  // ---------- Optimization panel ----------
  function trimContext() {
    const currentPath = editor.getCurrentPath();
    const softLimit = settings.contextSoftLimitTokens;
    const totalTokens = () =>
      Array.from(openFiles.values()).reduce((sum, f) => sum + estimateTokens(f.content), 0);

    const candidates = Array.from(openFiles.keys())
      .filter((path) => path !== currentPath)
      .sort((a, b) => (fileTouchedAt.get(a) ?? 0) - (fileTouchedAt.get(b) ?? 0)); // oldest-touched first

    let removed = 0;
    for (const path of candidates) {
      if (totalTokens() <= softLimit) break;
      openFiles.delete(path);
      fileTouchedAt.delete(path);
      removed++;
    }

    if (removed === 0) {
      alert(
        totalTokens() <= softLimit
          ? 'Open files are already under the context soft limit.'
          : "Nothing left to trim — only the file you're currently editing remains open.",
      );
      return;
    }
    alert(`Closed ${removed} least-recently-touched open file(s) to reduce context size. The file you're editing was kept open.`);
    panel.updateContextHealth(totalTokens(), selectedModel);
  }

  const panel = new OptimizationPanel(optimizationInner, settings, {
    onSettingsChanged: (next) => {
      settings = next;
      warmEmbedderIfEnabled(); // in case the user just turned A3 on
      warmCompressorIfEnabled(); // ...or B4
      void navRail.refresh(); // the rail shows how many stages are effectively on
      if (getStoredSession()) void pushSettings(next).catch(() => {});
    },
    onTrimContext: () => trimContext(),
  });

  // ---------- Chat pane ----------
  async function refreshConversationList() {
    const all = await getAllConversations();
    chat.setConversationList(
      all.map((c) => ({ id: c.id, title: c.title, updatedAt: c.updatedAt })),
      conversation.id,
    );
  }

  const chat = new ChatPane(chatPaneEl, selectedModel, {
    onSend: (message, optimized) => void handleSend(message, optimized),
    onRunComparison: (message, maxTokens) => void runComparisonBenchmark(message, maxTokens),
    onShowBenchmarkHistory: () => void showBenchmarkHistory(),
    onRunAgent: (message, personaId) => void runAgentLoop(message, personaId),
    onModelChange: (model) => {
      selectedModel = model;
      void saveSelectedModel(model);
    },
    onNewConversation: () => {
      conversation = newConversation();
      chat.renderHistory([]);
      void refreshConversationList();
    },
    onSelectConversation: (id) => {
      void (async () => {
        if (id === conversation.id) return;
        const record = await getConversation(id);
        if (!record) return;
        conversation = record;
        chat.renderHistory(conversation.turns);
        void refreshConversationList();
      })();
    },
    onApplyCodeBlock: (code) => {
      const path = editor.getCurrentPath();
      if (!path) {
        alert('Open a file first, then Apply.');
        return;
      }
      const existing = openFiles.get(path);
      let content = code;
      if (looksLikeUnifiedDiff(code)) {
        const patched = tryApplyUnifiedDiff(existing?.content ?? '', code);
        if (patched === null) {
          alert('This diff could not be applied cleanly to the currently open file.');
          return;
        }
        content = patched;
      }
      editor.replaceContent(content);
      openFiles.set(path, { path, content, languageId: existing?.languageId });
      void (async () => {
        const stored = await getFile(path);
        await saveFile({ ...stored, path, content, languageId: existing?.languageId ?? stored?.languageId, updatedAt: Date.now() });
      })();
    },
  });

  // D1's model-backed summarization hook — provider-agnostic from the pipeline's point of view
  // (just `(prompt, signal) => Promise<string>`); this is simply today's implementation of it via
  // the Anthropic API, using a cheap/fast model since it's a background task, not the main chat.
  async function summarizeWithModel(prompt: string, signal: AbortSignal): Promise<string> {
    const body: AnthropicRequestBody = {
      model: SUMMARIZATION_MODEL,
      max_tokens: 400,
      system: [
        {
          type: 'text',
          text: 'You summarize conversation excerpts concisely and factually. Output only the summary — no preamble, no markdown formatting.',
        },
      ],
      messages: [{ role: 'user', content: prompt }],
      stream: true,
    };
    let text = '';
    for await (const event of streamMessage(apiKey, body, signal)) {
      if (event.textDelta) text += event.textDelta;
    }
    if (!text.trim()) throw new Error('Empty summarization response');
    return text.trim();
  }

  function buildInitialDraft(userMessage: string, conv: ConversationRecord): DraftRequest {
    const contextBlocks: ContextBlock[] = Array.from(openFiles.values()).map((f) => ({
      id: `file:${f.path}`,
      label: f.path,
      content: f.content,
      cacheable: true,
    }));
    const history: ConversationTurn[] = conv.turns;
    const cursorPath = editor.getCurrentPath();
    const selection = cursorPath ? editor.getSelection() : null;
    const cursor = cursorPath && !selection ? editor.getCursor() : null;

    return {
      systemPrompt: SYSTEM_PROMPT,
      contextBlocks,
      history,
      userMessage,
      openFiles: Array.from(openFiles.values()),
      cursor: cursor && cursorPath ? { path: cursorPath, ...cursor } : undefined,
      selection: selection && cursorPath ? { path: cursorPath, ...selection } : undefined,
      sentFiles: conv.sentFiles,
      historySummary: conv.historySummary,
      historySummarizedThroughCount: conv.historySummarizedThroughCount,
    };
  }

  // `conversation` is reassigned synchronously by onNewConversation/onSelectConversation whenever
  // the user switches conversations — and handleSend spans two `await` points (the pipeline run,
  // then the streaming response) that can take seconds. Without pinning the conversation this send
  // actually belongs to up front, a switch mid-flight would silently attach the reply (and mutate
  // sentFiles/historySummary) onto whatever conversation happens to be active when each await
  // resolves, corrupting it with content from a completely different exchange. `targetConversation`
  // is that pin; every mutation below goes through it, and UI updates are gated on it still being
  // the visible one so a background send never bleeds into whatever the user navigated to instead.
  async function handleSend(message: string, optimized: boolean) {
    if (!apiKey) {
      alert('Add your Anthropic API key at the top of the Optimization panel first.');
      return;
    }

    const targetConversation = conversation;
    const initialDraft = buildInitialDraft(message, targetConversation);
    const enabledMap = optimized
      ? effectiveEnabledMap(settings, PIPELINE_STAGES)
      : Object.fromEntries(PIPELINE_STAGES.map((s) => [s.id, false]));

    const { draft: finalDraft, report } = await runPipeline(
      PIPELINE_STAGES,
      enabledMap,
      settings.params,
      initialDraft,
      2000,
      summarizeWithModel,
    );

    const requestBody = buildRequestBody(finalDraft, selectedModel, MAX_OUTPUT_TOKENS);
    let stillActive = targetConversation === conversation;
    if (stillActive) {
      panel.updatePreSendInspector(report, requestBody);
      const budget = (settings.params.A3?.budgetTokens as number | undefined) ?? 4000;
      panel.updateTokenBudgetReadout(report.totalTokensAfter, budget);
      panel.updateContextHealth(report.totalTokensAfter, selectedModel);
    }

    if (targetConversation.turns.length === 0) {
      targetConversation.title = message.slice(0, 60) || targetConversation.title;
    }
    if (stillActive) chat.appendMessage('user', message);
    targetConversation.turns.push({ role: 'user', content: message, ts: Date.now() });

    if (stillActive) chat.beginStreamingAssistantMessage();
    let usage: UsageInfo = {};
    let finalText = '';
    try {
      for await (const event of streamMessage(apiKey, requestBody)) {
        if (event.textDelta) {
          finalText += event.textDelta;
          stillActive = targetConversation === conversation;
          if (stillActive) chat.appendStreamingDelta(event.textDelta);
        }
        if (event.usage) usage = event.usage;
      }
    } catch (err) {
      const errText = `\n\n**Error:** ${err instanceof Error ? err.message : String(err)}`;
      finalText += errText;
      if (stillActive) chat.appendStreamingDelta(errText);
    }
    stillActive = targetConversation === conversation;
    if (stillActive) chat.endStreamingAssistantMessage();
    targetConversation.turns.push({ role: 'assistant', content: finalText, ts: Date.now() });
    targetConversation.updatedAt = Date.now();
    if (finalDraft.sentFilesThisRound) {
      targetConversation.sentFiles = { ...targetConversation.sentFiles, ...finalDraft.sentFilesThisRound };
    }
    if (finalDraft.historySummarizedThroughCountThisRound !== undefined) {
      targetConversation.historySummary = finalDraft.historySummary;
      targetConversation.historySummarizedThroughCount = finalDraft.historySummarizedThroughCountThisRound;
    }
    await saveConversation(targetConversation);
    if (getStoredSession()) void pushConversation(targetConversation).catch(() => {});
    void refreshConversationList();

    // If the user navigated away and back to this same conversation mid-stream, the incremental
    // appendStreamingDelta() calls above were skipped for that window — resync the visible
    // transcript from the now-complete, authoritative turns array rather than leave it truncated.
    if (targetConversation === conversation) {
      chat.renderHistory(targetConversation.turns);
      panel.updatePreSendInspector(report, requestBody, usage);
    }

    sessionStats.requests += 1;
    sessionStats.tokensSentEstimate += report.totalTokensAfter;
    sessionStats.baselineTokensEstimate += report.totalTokensBefore;
    sessionStats.inputTokens += usage.input_tokens ?? 0;
    sessionStats.outputTokens += usage.output_tokens ?? 0;
    sessionStats.cacheReadTokens += usage.cache_read_input_tokens ?? 0;
    sessionStats.cacheWriteTokens += usage.cache_creation_input_tokens ?? 0;
    sessionStats.estimatedCostUsd += costForUsage(usage, selectedModel);
    panel.updateSessionDashboard(sessionStats);
    chat.setSavings(sessionStats.baselineTokensEstimate, sessionStats.tokensSentEstimate);

    const cumulativeStats = await recordBenchmarkDelta({
      requests: 1,
      tokensBefore: report.totalTokensBefore,
      tokensAfter: report.totalTokensAfter,
      inputTokens: usage.input_tokens ?? 0,
      outputTokens: usage.output_tokens ?? 0,
      cacheReadTokens: usage.cache_read_input_tokens ?? 0,
      cacheWriteTokens: usage.cache_creation_input_tokens ?? 0,
      costUsd: costForUsage(usage, selectedModel),
    });
    benchmarksPanel.updateThisDevice(cumulativeStats);
    void navRail.refresh();
    if (getStoredSession()) void pushBenchmarks(cumulativeStats).catch(() => {});

    // Tier 1 telemetry: metadata only, anonymous, and deliberately NOT gated on a session — unlike
    // pushBenchmarks above, which is per-user. No tier2 here; that rides on Benchmark mode only.
    pushTelemetry({
      model: selectedModel,
      naulonVersion: __NAULON_VERSION__,
      settings,
      stageReport: report.stages,
      elapsedMs: report.elapsedMs,
      timedOut: report.timedOut,
      tokensBefore: report.totalTokensBefore,
      tokensAfter: report.totalTokensAfter,
      tokenCost: costForUsage(usage, selectedModel),
      tokenEstSaved: report.totalTokensSaved,
      userAgent: navigator.userAgent,
      languageMix: computeLanguageMix(Array.from(openFiles.keys())),
      agentMode: false,
    });
  }

  // ---------- Agent mode ----------
  // An agentic tool-use loop: the model gets read_file/list_files/search_files/write_file tools
  // over the same optimized context a normal send would build (respecting the user's current
  // pipeline settings — agent mode doesn't bypass the token-saving pipeline, it uses it), and can
  // make several tool-call round trips before giving a final answer. Non-streaming per round trip
  // (only the final answer needs to render live) with a hard cap on iterations so a confused model
  // can't loop forever. Tool-call steps are shown live in the transcript and folded into the
  // persisted assistant turn (as a blockquote log) so they survive a reload — unlike the benchmark
  // comparison tool's ephemeral run, this real conversation turn needs its content to be self-
  // contained for history.
  const MAX_AGENT_ITERATIONS = 8;

  async function runAgentLoop(message: string, personaId: string) {
    if (!apiKey) {
      alert('Add your Anthropic API key at the top of the Optimization panel first.');
      return;
    }

    // ContextTrees (opt-in, off by default) scopes this turn to a project's design-map branch and
    // hands back assembled roadmap context to send *alongside* the pipeline's output. It is a
    // supplementary layer: every failure below is swallowed so the user's message still gets sent
    // exactly as it would with the feature off. No project-picker UI exists yet, so an enabled
    // feature with no active project lazily bootstraps a default one.
    const ctSettings = await getContextTreesSettings();

    const targetConversation = conversation;
    const initialDraft = buildInitialDraft(message, targetConversation);
    const enabledMap = effectiveEnabledMap(settings, PIPELINE_STAGES);

    const { draft: finalDraft, report } = await runPipeline(
      PIPELINE_STAGES,
      enabledMap,
      settings.params,
      initialDraft,
      2000,
      summarizeWithModel,
    );

    let stillActive = targetConversation === conversation;
    if (stillActive) {
      const budget = (settings.params.A3?.budgetTokens as number | undefined) ?? 4000;
      panel.updateTokenBudgetReadout(report.totalTokensAfter, budget);
      panel.updateContextHealth(report.totalTokensAfter, selectedModel);
    }

    if (targetConversation.turns.length === 0) {
      targetConversation.title = message.slice(0, 60) || targetConversation.title;
    }
    if (stillActive) chat.appendMessage('user', message);
    targetConversation.turns.push({ role: 'user', content: message, ts: Date.now() });

    // The director's dispatch decision. With ContextTrees off this is exactly the pre-dispatch path:
    // the manually-picked persona handles the whole message, with no router call and so no extra
    // latency or cost. With it on, the project is opened once and shared by every dispatched agent.
    let ctDirector: Director | undefined;
    let ctProjectId: string | undefined;
    let ctRootNodeId: string | undefined;
    let plan: DispatchPlanEntry[] = [{ personaId, task: message }];

    if (ctSettings.enabled) {
      try {
        let projectId = await getActiveContextTreesProjectId();
        const projects = await listContextTreesProjects();
        let meta = projectId ? projects.find((p) => p.id === projectId) : undefined;
        if (!meta) {
          meta = projects[0] ?? (await createContextTreesProject('Default'));
          projectId = meta.id;
          await setActiveContextTreesProjectId(projectId);
        }

        ctDirector = await loadContextTreesDirector(meta.id);
        ctProjectId = meta.id;
        ctRootNodeId = meta.rootNodeId;
      } catch (err) {
        console.warn('ContextTrees: could not open a project for this turn; continuing without it.', err);
      }

      // Total by construction: any failure inside falls back to the selected persona handling the
      // whole message, i.e. the single-agent shape above.
      plan = await planAgentDispatch({ apiKey, message, personas: AGENT_PERSONAS, defaultPersonaId: personaId });
    }

    // One pipeline run for the whole turn: it optimizes the shared conversation context, which is
    // identical for every dispatched agent. Each agent layers its own persona prompt and its own
    // session's context text on top of this same base.
    const baseSystemBlocks = buildSystemBlocks(finalDraft);

    // Shared across concurrent agents because it holds no per-turn state — just a callback keeping
    // the open editor buffer (and in-memory context for the next send) in sync when an agent writes
    // the file currently being viewed. The *permission* fields are deliberately not shared; they are
    // per-persona enforcement and are rebuilt per dispatch entry below.
    const onFileWritten: NonNullable<ToolExecutionContext['onFileWritten']> = (path, content) => {
      if (editor.getCurrentPath() === path) editor.replaceContent(content);
      const existing = openFiles.get(path);
      if (existing) openFiles.set(path, { ...existing, content });
    };

    interface AgentRunResult {
      persona: AgentPersona;
      toolCallLog: string[];
      finalText: string;
      usage: UsageInfo;
      /** Absent when this agent's session could not be started; it then skips reconciliation. */
      ctSession?: MicroSession;
      ctAgent?: AgentDescriptor;
    }

    /** One dispatched agent's whole tool-use loop. Everything mutable in here is per-call, because
     * entries run concurrently. */
    async function runOneAgent(entry: DispatchPlanEntry): Promise<AgentRunResult> {
      const persona = getPersona(entry.personaId);

      // Every dispatched agent gets its own micro-session, all on the same project root node —
      // there is still exactly one node until a tree-editing UI exists. A session that fails to
      // start costs this agent its context block and its reconciliation, never the turn.
      let ctSession: MicroSession | undefined;
      let ctAgent: AgentDescriptor | undefined;
      if (ctDirector && ctRootNodeId) {
        try {
          const harness = ensurePersonaHarness(ctDirector, persona);
          const agent = equipAgent('micro', harness);
          ctSession = ctDirector.startSession({
            description: entry.task.slice(0, 120),
            branchNodeId: ctRootNodeId,
            agents: [agent],
            reviewMode: { ...ctSettings.reviewMode },
          });
          ctAgent = agent;
        } catch (err) {
          console.warn('ContextTrees: could not start a session for this agent; continuing without it.', err);
        }
      }

      const tools = persona.allowedTools.map((name) => AGENT_TOOLS[name]).filter(Boolean);
      const systemBlocks = [...baseSystemBlocks];
      if (persona.systemPromptAddition) systemBlocks.push({ type: 'text', text: persona.systemPromptAddition });
      if (ctSession?.contextText) systemBlocks.push({ type: 'text', text: ctSession.contextText });

      const toolCtx: ToolExecutionContext = {
        allowedTools: new Set(persona.allowedTools),
        canWritePath: persona.canWritePath,
        onFileWritten,
      };

      // The pipeline rewrote the *whole* user message, so its output is only usable by an agent
      // handling that whole message; a split sub-task sends its own instruction verbatim.
      const userText = entry.task === message ? finalDraft.userMessage : entry.task;
      const messages: AnthropicMessage[] = finalDraft.history.map((t) => ({ role: t.role, content: t.content }));
      messages.push({ role: 'user', content: userText });

      const toolCallLog: string[] = [];
      let finalText = '';
      let usage: UsageInfo = {};

      try {
        for (let iteration = 1; iteration <= MAX_AGENT_ITERATIONS; iteration++) {
          const response = await sendMessageOnce(apiKey, {
            model: selectedModel,
            max_tokens: MAX_OUTPUT_TOKENS,
            system: systemBlocks,
            messages,
            stream: false,
            tools,
          });

          usage = {
            input_tokens: (usage.input_tokens ?? 0) + (response.usage.input_tokens ?? 0),
            output_tokens: (usage.output_tokens ?? 0) + (response.usage.output_tokens ?? 0),
            cache_creation_input_tokens:
              (usage.cache_creation_input_tokens ?? 0) + (response.usage.cache_creation_input_tokens ?? 0),
            cache_read_input_tokens: (usage.cache_read_input_tokens ?? 0) + (response.usage.cache_read_input_tokens ?? 0),
          };
          messages.push({ role: 'assistant', content: response.content });

          const toolUseBlocks = response.content.filter(
            (b): b is Extract<AnthropicContentBlock, { type: 'tool_use' }> => b.type === 'tool_use',
          );
          const text = response.content
            .filter((b): b is Extract<AnthropicContentBlock, { type: 'text' }> => b.type === 'text')
            .map((b) => b.text)
            .join('');

          if (toolUseBlocks.length === 0) {
            finalText = text || '(empty response)';
            break;
          }

          const toolResults: AnthropicContentBlock[] = [];
          for (const block of toolUseBlocks) {
            const inputSummary = JSON.stringify(block.input).slice(0, 100);
            const line = `🔧 \`${block.name}(${inputSummary})\``;
            toolCallLog.push(line);
            // Live status lines from concurrent agents interleave, so they get attributed — but only
            // when there is actually more than one agent, keeping the common case unchanged.
            if (targetConversation === conversation) {
              chat.appendAgentStatus(plan.length > 1 ? `**${persona.label}** ${line}` : line);
            }

            const result = await executeTool(block.name, block.input, toolCtx);
            toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: result.content, is_error: result.isError });
          }
          messages.push({ role: 'user', content: toolResults });

          if (iteration === MAX_AGENT_ITERATIONS) {
            finalText = text || 'Stopped after reaching the maximum number of tool-call steps for one turn.';
          }
        }
      } catch (err) {
        finalText = `Error: ${err instanceof Error ? err.message : String(err)}`;
      }

      return { persona, toolCallLog, finalText, usage, ctSession, ctAgent };
    }

    const results = await Promise.all(plan.map((entry) => runOneAgent(entry)));

    // The bookkeeping below reads a single usage figure, so the fan-out is summed back into one.
    const totalUsage = results.reduce<UsageInfo>(
      (acc, r) => ({
        input_tokens: (acc.input_tokens ?? 0) + (r.usage.input_tokens ?? 0),
        output_tokens: (acc.output_tokens ?? 0) + (r.usage.output_tokens ?? 0),
        cache_creation_input_tokens: (acc.cache_creation_input_tokens ?? 0) + (r.usage.cache_creation_input_tokens ?? 0),
        cache_read_input_tokens: (acc.cache_read_input_tokens ?? 0) + (r.usage.cache_read_input_tokens ?? 0),
      }),
      {},
    );

    const withToolCallLog = (r: AgentRunResult) =>
      r.toolCallLog.length ? `${r.toolCallLog.map((l) => `> ${l}`).join('\n')}\n\n${r.finalText}` : r.finalText;

    // A single agent renders exactly as it did before dispatch existed. Only a genuine split adds
    // per-agent headings, which the chat pane already renders as markdown.
    const finalTextWithLog =
      results.length === 1
        ? withToolCallLog(results[0])
        : results.map((r) => `### ${r.persona.label}\n\n${withToolCallLog(r)}`).join('\n\n---\n\n');

    stillActive = targetConversation === conversation;
    if (stillActive) chat.appendMessage('assistant', finalTextWithLog);
    targetConversation.turns.push({ role: 'assistant', content: finalTextWithLog, ts: Date.now() });
    targetConversation.updatedAt = Date.now();
    if (finalDraft.sentFilesThisRound) {
      targetConversation.sentFiles = { ...targetConversation.sentFiles, ...finalDraft.sentFilesThisRound };
    }
    if (finalDraft.historySummarizedThroughCountThisRound !== undefined) {
      targetConversation.historySummary = finalDraft.historySummary;
      targetConversation.historySummarizedThroughCount = finalDraft.historySummarizedThroughCountThisRound;
    }

    // Reconcile each session back into the design map / agent memory. Same reasoning as the session
    // start block: bookkeeping never gets to stop the conversation being saved, so this is fully
    // contained — including the manual-review await, which the user can dismiss without applying.
    // Sequential on purpose: every entry mutates the same in-memory Director, so concurrent
    // endSession calls against it would race. One snapshot is written after the whole loop.
    if (ctDirector && ctProjectId && ctRootNodeId) {
      targetConversation.projectId = ctProjectId;
      for (const r of results) {
        if (!r.ctSession || !r.ctAgent) continue;
        try {
          const reconciliationInput = {
            session: r.ctSession,
            agent: r.ctAgent,
            toolCallLog: r.toolCallLog,
            description: r.ctSession.description,
            rootNodeId: ctRootNodeId,
            director: ctDirector,
          };
          const endInput =
            ctSettings.updateAuthoring === 'llm'
              ? await authorReconciliationWithLLM({ ...reconciliationInput, apiKey, finalText: r.finalText })
              : buildHeuristicReconciliation(reconciliationInput);

          const outcome = await ctDirector.endSession(r.ctSession, endInput);
          await reviewSessionOutcome(outcome);
        } catch (err) {
          console.warn('ContextTrees: session reconciliation failed; the conversation is unaffected.', err);
        }
      }
      try {
        await saveContextTreesDirector(ctProjectId, ctDirector);
      } catch (err) {
        console.warn('ContextTrees: saving the project snapshot failed; the conversation is unaffected.', err);
      }
    }

    await saveConversation(targetConversation);
    if (getStoredSession()) void pushConversation(targetConversation).catch(() => {});
    void refreshConversationList();

    if (targetConversation === conversation) {
      chat.renderHistory(targetConversation.turns);
    }

    sessionStats.requests += 1;
    sessionStats.tokensSentEstimate += report.totalTokensAfter;
    sessionStats.baselineTokensEstimate += report.totalTokensBefore;
    sessionStats.inputTokens += totalUsage.input_tokens ?? 0;
    sessionStats.outputTokens += totalUsage.output_tokens ?? 0;
    sessionStats.cacheReadTokens += totalUsage.cache_read_input_tokens ?? 0;
    sessionStats.cacheWriteTokens += totalUsage.cache_creation_input_tokens ?? 0;
    sessionStats.estimatedCostUsd += costForUsage(totalUsage, selectedModel);
    panel.updateSessionDashboard(sessionStats);
    chat.setSavings(sessionStats.baselineTokensEstimate, sessionStats.tokensSentEstimate);

    const cumulativeStats = await recordBenchmarkDelta({
      requests: 1,
      tokensBefore: report.totalTokensBefore,
      tokensAfter: report.totalTokensAfter,
      inputTokens: totalUsage.input_tokens ?? 0,
      outputTokens: totalUsage.output_tokens ?? 0,
      cacheReadTokens: totalUsage.cache_read_input_tokens ?? 0,
      cacheWriteTokens: totalUsage.cache_creation_input_tokens ?? 0,
      costUsd: costForUsage(totalUsage, selectedModel),
    });
    benchmarksPanel.updateThisDevice(cumulativeStats);
    void navRail.refresh();
    if (getStoredSession()) void pushBenchmarks(cumulativeStats).catch(() => {});

    // Same Tier 1 payload as handleSend, tagged with the agent persona — agent mode runs the same
    // pipeline, so its stage report is directly comparable to a regular send's.
    pushTelemetry({
      model: selectedModel,
      naulonVersion: __NAULON_VERSION__,
      settings,
      stageReport: report.stages,
      elapsedMs: report.elapsedMs,
      timedOut: report.timedOut,
      tokensBefore: report.totalTokensBefore,
      tokensAfter: report.totalTokensAfter,
      tokenCost: costForUsage(totalUsage, selectedModel),
      tokenEstSaved: report.totalTokensSaved,
      userAgent: navigator.userAgent,
      languageMix: computeLanguageMix(Array.from(openFiles.keys())),
      agentMode: true,
      agentPersona: personaId,
    });
  }

  // ---------- Benchmark comparison tool ----------
  // A side experiment, not a normal send: compares the optimized pipeline (run exactly as the
  // user currently has it configured — no override) against a naive, unranked baseline, with the
  // baseline's context truncated to match whatever token count the optimized side actually used
  // (capped at the user-specified max) so both sides get a symmetric amount of context — the only
  // variable being *how* it was selected, not how much of it there is. Deliberately not saved to
  // conversation history — it's for comparing output quality/cost, not part of the chat itself.
  // Every completed run IS auto-saved separately (see saveBenchmarkRun) so past comparisons can be
  // revisited later via the "History" button.
  interface ComparisonColumnData {
    title: string;
    tokensBefore: number;
    tokensAfter: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
    elapsedMs: number;
    responseText: string;
    contextBlocks: { label: string; content: string }[];
  }

  async function sendComparisonRequest(requestBody: AnthropicRequestBody): Promise<{ text: string; usage: UsageInfo; elapsedMs: number }> {
    const start = performance.now();
    let text = '';
    let usage: UsageInfo = {};
    try {
      for await (const event of streamMessage(apiKey, requestBody)) {
        if (event.textDelta) text += event.textDelta;
        if (event.usage) usage = event.usage;
      }
    } catch (err) {
      text = `Error: ${err instanceof Error ? err.message : String(err)}`;
    }
    return { text, usage, elapsedMs: performance.now() - start };
  }

  function renderComparisonColumn(data: ComparisonColumnData): HTMLElement {
    const col = document.createElement('div');
    col.className = 'comparison-column';
    const h = document.createElement('h4');
    h.textContent = data.title;
    col.appendChild(h);

    const stats = document.createElement('div');
    stats.className = 'stats';
    stats.innerHTML = `
      <span>Context tokens: ${data.tokensBefore.toLocaleString()} → ${data.tokensAfter.toLocaleString()}</span>
      <span>Input / output tokens: ${data.inputTokens.toLocaleString()} / ${data.outputTokens.toLocaleString()}</span>
      <span>Est. cost: $${data.costUsd.toFixed(4)}</span>
      <span>Time: ${(data.elapsedMs / 1000).toFixed(1)}s</span>`;
    col.appendChild(stats);

    const response = document.createElement('div');
    response.className = 'response';
    response.textContent = data.responseText;
    col.appendChild(response);

    const contextDetails = document.createElement('details');
    contextDetails.className = 'comparison-context';
    const totalChars = data.contextBlocks.reduce((sum, b) => sum + b.content.length, 0);
    contextDetails.innerHTML = `<summary>Context sent (${data.contextBlocks.length} block${data.contextBlocks.length === 1 ? '' : 's'}, ${totalChars.toLocaleString()} chars)</summary>`;
    for (const block of data.contextBlocks) {
      const blockEl = document.createElement('div');
      blockEl.className = 'comparison-context-block';
      const label = document.createElement('div');
      label.className = 'comparison-context-label';
      label.textContent = block.label;
      const content = document.createElement('pre');
      content.textContent = block.content;
      blockEl.appendChild(label);
      blockEl.appendChild(content);
      contextDetails.appendChild(blockEl);
    }
    col.appendChild(contextDetails);

    return col;
  }

  async function runComparisonBenchmark(message: string, maxTokens: number) {
    if (!apiKey) {
      alert('Add your Anthropic API key at the top of the Optimization panel first.');
      return;
    }

    const baseDraft = buildInitialDraft(message, conversation);

    const modal = openModal('Benchmark comparison');
    const promptEl = document.createElement('div');
    promptEl.className = 'comparison-prompt';
    promptEl.innerHTML = '<div class="comparison-prompt-label">Prompt</div>';
    const promptText = document.createElement('div');
    promptText.className = 'comparison-prompt-text';
    promptText.textContent = message;
    promptEl.appendChild(promptText);
    modal.bodyEl.appendChild(promptEl);

    const statusEl = document.createElement('div');
    statusEl.className = 'comparison-status';
    statusEl.textContent = 'Assembling context…';
    modal.bodyEl.appendChild(statusEl);

    // Optimized side: exactly the user's current settings, unmodified — this run determines how
    // much context the optimized approach actually needs, which the naive baseline then matches.
    const optimizedEnabled = effectiveEnabledMap(settings, PIPELINE_STAGES);
    const { draft: optimizedDraft, report: optimizedReport } = await runPipeline(
      PIPELINE_STAGES,
      optimizedEnabled,
      settings.params,
      structuredClone(baseDraft),
      2000,
      summarizeWithModel,
    );

    // Symmetric context: the naive baseline is truncated to whatever the optimized side actually
    // used, never exceeding the user's specified max either way.
    const symmetricTarget = Math.min(optimizedReport.totalTokensAfter, maxTokens);
    const baselineDraft = truncateContextToTokenBudget(structuredClone(baseDraft), symmetricTarget);
    const baselineReport: PipelineRunReport = {
      stages: [],
      totalTokensBefore: estimateTokens(draftText(baseDraft)),
      totalTokensAfter: estimateTokens(draftText(baselineDraft)),
      totalTokensSaved: 0,
      timedOut: false,
      elapsedMs: 0,
    };

    statusEl.textContent = 'Sending both requests in parallel…';

    const optimizedRequestBody = buildRequestBody(optimizedDraft, selectedModel, MAX_OUTPUT_TOKENS);
    const baselineRequestBody = buildRequestBody(baselineDraft, selectedModel, MAX_OUTPUT_TOKENS);

    const [optimizedSend, baselineSend] = await Promise.all([
      sendComparisonRequest(optimizedRequestBody),
      sendComparisonRequest(baselineRequestBody),
    ]);

    const optimizedData: ComparisonColumnData = {
      title: 'Optimized (your current settings)',
      tokensBefore: optimizedReport.totalTokensBefore,
      tokensAfter: optimizedReport.totalTokensAfter,
      inputTokens: optimizedSend.usage.input_tokens ?? 0,
      outputTokens: optimizedSend.usage.output_tokens ?? 0,
      costUsd: costForUsage(optimizedSend.usage, selectedModel),
      elapsedMs: optimizedSend.elapsedMs,
      responseText: optimizedSend.text,
      contextBlocks: optimizedDraft.contextBlocks.map((b) => ({ label: b.label, content: b.content })),
    };
    const baselineData: ComparisonColumnData = {
      title: `Baseline (naive, matched @ ${symmetricTarget.toLocaleString()} tok)`,
      tokensBefore: baselineReport.totalTokensBefore,
      tokensAfter: baselineReport.totalTokensAfter,
      inputTokens: baselineSend.usage.input_tokens ?? 0,
      outputTokens: baselineSend.usage.output_tokens ?? 0,
      costUsd: costForUsage(baselineSend.usage, selectedModel),
      elapsedMs: baselineSend.elapsedMs,
      responseText: baselineSend.text,
      contextBlocks: baselineDraft.contextBlocks.map((b) => ({ label: b.label, content: b.content })),
    };

    statusEl.remove();
    const columns = document.createElement('div');
    columns.className = 'comparison-columns';
    columns.appendChild(renderComparisonColumn(optimizedData));
    columns.appendChild(renderComparisonColumn(baselineData));
    modal.bodyEl.appendChild(columns);

    const note = document.createElement('div');
    note.className = 'comparison-note';
    note.textContent = 'This run has been saved — open it again later via "History".';
    modal.bodyEl.appendChild(note);

    // The fully-assembled request text for each side — the "promptProcessed" both the local run
    // record and Tier 2 want, distinct from the raw prompt and from the context blocks alone.
    const optimizedPromptProcessed = draftText(optimizedDraft);
    const baselinePromptProcessed = draftText(baselineDraft);

    // Running Benchmark comparison mode IS the Tier 2 opt-in, so this single call carries both
    // tiers: Tier 1 metadata from the optimized variant, plus the actual content. Sent before the
    // local save so a storage failure can't cost the submission.
    pushTelemetry({
      model: selectedModel,
      naulonVersion: __NAULON_VERSION__,
      settings,
      stageReport: optimizedReport.stages,
      elapsedMs: optimizedReport.elapsedMs,
      timedOut: optimizedReport.timedOut,
      tokensBefore: optimizedReport.totalTokensBefore,
      tokensAfter: optimizedReport.totalTokensAfter,
      tokenCost: optimizedData.costUsd,
      tokenEstSaved: optimizedReport.totalTokensSaved,
      userAgent: navigator.userAgent,
      languageMix: computeLanguageMix(Array.from(openFiles.keys())),
      agentMode: false,
      tier2: {
        prompt: message,
        promptProcessed: optimizedPromptProcessed,
        context: optimizedDraft.contextBlocks.map((b) => ({ label: b.label, content: b.content })),
        output: optimizedData.responseText,
        tokenOrigCost: baselineData.costUsd,
      },
    });

    await saveBenchmarkRun({
      prompt: message,
      model: selectedModel,
      settings,
      optimized: {
        label: optimizedData.title,
        contextBlocks: optimizedDraft.contextBlocks,
        promptProcessed: optimizedPromptProcessed,
        tokensBefore: optimizedData.tokensBefore,
        tokensAfter: optimizedData.tokensAfter,
        responseText: optimizedData.responseText,
        inputTokens: optimizedData.inputTokens,
        outputTokens: optimizedData.outputTokens,
        costUsd: optimizedData.costUsd,
        elapsedMs: optimizedData.elapsedMs,
      },
      baseline: {
        label: baselineData.title,
        contextBlocks: baselineDraft.contextBlocks,
        promptProcessed: baselinePromptProcessed,
        tokensBefore: baselineData.tokensBefore,
        tokensAfter: baselineData.tokensAfter,
        responseText: baselineData.responseText,
        inputTokens: baselineData.inputTokens,
        outputTokens: baselineData.outputTokens,
        costUsd: baselineData.costUsd,
        elapsedMs: baselineData.elapsedMs,
      },
    });
  }

  function benchmarkRunToColumnData(run: BenchmarkVariantRecord, contents: Map<string, string>): ComparisonColumnData {
    return {
      title: run.label,
      tokensBefore: run.tokensBefore,
      tokensAfter: run.tokensAfter,
      inputTokens: run.inputTokens,
      outputTokens: run.outputTokens,
      costUsd: run.costUsd,
      elapsedMs: run.elapsedMs,
      responseText: run.responseText,
      contextBlocks: run.contextRefs.map((ref) => ({ label: ref.label, content: contents.get(ref.hash) ?? '(context no longer available)' })),
    };
  }

  async function openBenchmarkRun(run: BenchmarkRunRecord) {
    const allRefs = [...run.optimized.contextRefs, ...run.baseline.contextRefs];
    const uniqueHashes = Array.from(new Set(allRefs.map((r) => r.hash)));
    const resolved = await Promise.all(uniqueHashes.map(async (hash) => [hash, await resolveContextBlock(hash)] as const));
    const contents = new Map(resolved.map(([hash, content]) => [hash, content ?? '(context no longer available)']));

    const modal = openModal('Benchmark comparison — ' + new Date(run.createdAt).toLocaleString());
    const promptEl = document.createElement('div');
    promptEl.className = 'comparison-prompt';
    promptEl.innerHTML = '<div class="comparison-prompt-label">Prompt</div>';
    const promptText = document.createElement('div');
    promptText.className = 'comparison-prompt-text';
    promptText.textContent = run.prompt;
    promptEl.appendChild(promptText);
    modal.bodyEl.appendChild(promptEl);

    const columns = document.createElement('div');
    columns.className = 'comparison-columns';
    columns.appendChild(renderComparisonColumn(benchmarkRunToColumnData(run.optimized, contents)));
    columns.appendChild(renderComparisonColumn(benchmarkRunToColumnData(run.baseline, contents)));
    modal.bodyEl.appendChild(columns);
  }

  async function showBenchmarkHistory() {
    const runs = await getBenchmarkRuns();
    const modal = openModal('Benchmark history');
    if (runs.length === 0) {
      modal.bodyEl.innerHTML = '<div class="comparison-status">No saved benchmark runs yet.</div>';
      return;
    }
    const list = document.createElement('div');
    list.className = 'benchmark-history-list';
    for (const run of runs) {
      const row = document.createElement('button');
      row.className = 'btn subtle benchmark-history-row';
      const when = new Date(run.createdAt).toLocaleString();
      row.innerHTML = `<span class="benchmark-history-prompt">${run.prompt.slice(0, 80)}</span><span class="benchmark-history-meta">${when} · ${run.model}</span>`;
      row.addEventListener('click', () => {
        modal.close();
        void openBenchmarkRun(run);
      });
      list.appendChild(row);
    }
    modal.bodyEl.appendChild(list);
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  void refreshConversationList();
}

void main();
